import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from '../../hooks/use-toast';

const AboutUsManager = () => {
  const [content, setContent] = useState({
    title: '',
    intro_paragraph_1: '',
    intro_paragraph_2: '',
    intro_paragraph_3: '',
    mission_title: '',
    mission_text: '',
    vision_title: '',
    vision_text: '',
    strategic_goals_title: '',
    strategic_goals_paragraph_1: '',
    strategic_goals_paragraph_2: '',
    why_title: '',
    why_benefit_1: '',
    why_benefit_2: '',
    why_benefit_3: '',
    why_benefit_4: '',
    closing_statement: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/about-us`);
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      }
    } catch (error) {
      console.error('Error fetching About Us content:', error);
      toast({
        title: 'Error',
        description: 'Failed to load About Us content',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/about-us`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(content),
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'About Us page updated successfully',
        });
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error saving About Us content:', error);
      toast({
        title: 'Error',
        description: 'Failed to update About Us content',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContent({
      ...content,
      [field]: value,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border-2 border-red-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
        <div className="bg-gradient-to-r from-red-50 to-gray-50 dark:from-red-900/20 dark:to-gray-800 px-6 py-4 rounded-t-lg border-b border-red-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-red-700 dark:text-red-400">About Us Page Editor</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage the content displayed on the About Us page
          </p>
        </div>
        <div className="pt-6 px-6 pb-6 space-y-6">
          {/* Page Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Page Title
            </label>
            <Input
              value={content.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="ABOUT US"
            />
          </div>

          {/* Introduction Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-red-400 mb-4">Introduction</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Paragraph 1
                </label>
                <Textarea
                  value={content.intro_paragraph_1}
                  onChange={(e) => handleChange('intro_paragraph_1', e.target.value)}
                  rows={4}
                  placeholder="First introduction paragraph..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Paragraph 2
                </label>
                <Textarea
                  value={content.intro_paragraph_2}
                  onChange={(e) => handleChange('intro_paragraph_2', e.target.value)}
                  rows={4}
                  placeholder="Second introduction paragraph..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Paragraph 3
                </label>
                <Textarea
                  value={content.intro_paragraph_3}
                  onChange={(e) => handleChange('intro_paragraph_3', e.target.value)}
                  rows={4}
                  placeholder="Third introduction paragraph..."
                />
              </div>
            </div>
          </div>

          {/* Mission & Vision */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-red-400 mb-4">Mission & Vision</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Mission Title
                  </label>
                  <Input
                    value={content.mission_title}
                    onChange={(e) => handleChange('mission_title', e.target.value)}
                    placeholder="Our Mission"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Mission Text
                  </label>
                  <Textarea
                    value={content.mission_text}
                    onChange={(e) => handleChange('mission_text', e.target.value)}
                    rows={4}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Vision Title
                  </label>
                  <Input
                    value={content.vision_title}
                    onChange={(e) => handleChange('vision_title', e.target.value)}
                    placeholder="Our Vision"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Vision Text
                  </label>
                  <Textarea
                    value={content.vision_text}
                    onChange={(e) => handleChange('vision_text', e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Strategic Goals */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-red-400 mb-4">Strategic Goals</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Section Title
                </label>
                <Input
                  value={content.strategic_goals_title}
                  onChange={(e) => handleChange('strategic_goals_title', e.target.value)}
                  placeholder="Strategic Goals"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Paragraph 1
                </label>
                <Textarea
                  value={content.strategic_goals_paragraph_1}
                  onChange={(e) => handleChange('strategic_goals_paragraph_1', e.target.value)}
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Paragraph 2
                </label>
                <Textarea
                  value={content.strategic_goals_paragraph_2}
                  onChange={(e) => handleChange('strategic_goals_paragraph_2', e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Why Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-red-400 mb-4">Why TrainerList?</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Section Title
                </label>
                <Input
                  value={content.why_title}
                  onChange={(e) => handleChange('why_title', e.target.value)}
                  placeholder="Why TrainerList?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Benefit 1
                </label>
                <Input
                  value={content.why_benefit_1}
                  onChange={(e) => handleChange('why_benefit_1', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Benefit 2
                </label>
                <Input
                  value={content.why_benefit_2}
                  onChange={(e) => handleChange('why_benefit_2', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Benefit 3
                </label>
                <Input
                  value={content.why_benefit_3}
                  onChange={(e) => handleChange('why_benefit_3', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Benefit 4
                </label>
                <Input
                  value={content.why_benefit_4}
                  onChange={(e) => handleChange('why_benefit_4', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Closing Statement */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-red-400 mb-4">Closing Statement</h3>
            <Textarea
              value={content.closing_statement}
              onChange={(e) => handleChange('closing_statement', e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Preview Card */}
      <Card className="border-2 border-blue-200 dark:border-gray-700">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-gray-700 dark:to-gray-800">
          <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
            <Eye className="w-5 h-5" />
            Preview
          </CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Preview how changes will appear on the page
          </p>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="prose max-w-none dark:prose-invert">
            <h1 className="text-3xl font-bold text-center text-red-700 dark:text-red-400 mb-6">{content.title}</h1>
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>{content.intro_paragraph_1}</p>
              <p>{content.intro_paragraph_2}</p>
              <p>{content.intro_paragraph_3}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 my-6">
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border dark:border-red-800">
                <h3 className="font-bold text-red-700 dark:text-red-400">{content.mission_title}</h3>
                <p className="text-sm mt-2 text-gray-700 dark:text-gray-300">{content.mission_text}</p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border dark:border-red-800">
                <h3 className="font-bold text-red-700 dark:text-red-400">{content.vision_title}</h3>
                <p className="text-sm mt-2 text-gray-700 dark:text-gray-300">{content.vision_text}</p>
              </div>
            </div>
            
            {/* Strategic Goals Preview */}
            <div className="my-6 bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border dark:border-red-800">
              <h3 className="font-bold text-red-700 dark:text-red-400 mb-2">{content.strategic_goals_title}</h3>
              <p className="text-sm mt-2 text-gray-700 dark:text-gray-300">{content.strategic_goals_paragraph_1}</p>
              <p className="text-sm mt-2 text-gray-700 dark:text-gray-300">{content.strategic_goals_paragraph_2}</p>
            </div>
            
            <div className="my-6">
              <h3 className="font-bold text-red-700 dark:text-red-400 mb-2">{content.why_title}</h3>
              <ul className="text-sm space-y-1 text-gray-700 dark:text-gray-300">
                <li>✓ {content.why_benefit_1}</li>
                <li>✓ {content.why_benefit_2}</li>
                <li>✓ {content.why_benefit_3}</li>
                <li>✓ {content.why_benefit_4}</li>
              </ul>
            </div>
            
            <p className="text-center font-semibold text-gray-800 dark:text-white bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border dark:border-red-800">
              {content.closing_statement}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AboutUsManager;
